const DEFAULT_SPAN_MS = 8000;
const MIN_SPAN_MS = 200;
const HANDLE_PX = 6;
const MIN_REGION_MS = 20;
/** Stable hue per label so classification colours never shuffle. */
export function labelHue(label) {
    let h = 2166136261;
    for (let i = 0; i < label.length; i++) {
        h ^= label.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return Math.abs(h % 360);
}
/**
 * `<ac-live-timeline>` — a realtime, scrollable volume time-series graph.
 *
 * Unlike a level meter it retains the full history of the take, so the same
 * widget is used to (a) watch the signal extend live while recording,
 * (b) navigate/scrub for replay, and (c) draw + label phoneme regions. The
 * background of every frame column is tinted with the colour of the live
 * classifier's label for that frame.
 */
export class AcLiveTimeline extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "c2d", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "samples", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "regions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "durationMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "viewStartMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "viewSpanMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_SPAN_MS
        });
        Object.defineProperty(this, "follow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "playheadMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "selectedId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "frameMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 10
        });
        Object.defineProperty(this, "drag", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { kind: "none" }
        });
        Object.defineProperty(this, "draft", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "raf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "dirty", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "dpr", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "ro", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "liveBtn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onWheel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                e.preventDefault();
                const rect = this.canvas.getBoundingClientRect();
                const anchor = this.xToTime(e.clientX - rect.left);
                if (e.ctrlKey || e.metaKey || e.shiftKey) {
                    const factor = Math.exp(e.deltaY * 0.0015);
                    const newSpan = Math.max(MIN_SPAN_MS, Math.min(Math.max(this.durationMs, DEFAULT_SPAN_MS) * 2, this.viewSpanMs * factor));
                    const ratio = (anchor - this.viewStartMs) / this.viewSpanMs;
                    this.viewSpanMs = newSpan;
                    this.viewStartMs = Math.max(0, anchor - ratio * newSpan);
                    this.follow = false;
                }
                else {
                    const delta = (e.deltaY || e.deltaX) * (this.viewSpanMs / this.cssW);
                    this.viewStartMs = Math.max(0, this.viewStartMs + delta);
                    this.follow = false;
                }
                this.emit("timeline-follow-changed", { follow: this.follow });
                this.markDirty();
            }
        });
        Object.defineProperty(this, "onPointerDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                this.canvas?.setPointerCapture(e.pointerId);
                this.focus();
                const rect = this.canvas.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const t = Math.max(0, this.xToTime(x));
                if (e.shiftKey) {
                    this.drag = { kind: "create", startMs: t, endMs: t };
                    this.markDirty();
                    return;
                }
                if (e.altKey) {
                    this.drag = { kind: "pan", startX: x, startViewMs: this.viewStartMs };
                    this.follow = false;
                    this.emit("timeline-follow-changed", { follow: false });
                    return;
                }
                const hit = this.hitRegion(x);
                if (hit) {
                    this.selectedId = hit.region.id;
                    this.emit("timeline-region-selected", { regionId: hit.region.id });
                    this.draft = {
                        id: hit.region.id,
                        startMs: hit.region.startMs,
                        endMs: hit.region.endMs,
                    };
                    this.drag = hit.edge
                        ? { kind: "resize", id: hit.region.id, edge: hit.edge }
                        : {
                            kind: "move",
                            id: hit.region.id,
                            grabMs: t,
                            origStartMs: hit.region.startMs,
                            origEndMs: hit.region.endMs,
                        };
                    this.markDirty();
                    return;
                }
                this.selectedId = null;
                this.emit("timeline-region-selected", { regionId: null });
                this.drag = { kind: "scrub" };
                this.follow = false;
                this.emit("timeline-follow-changed", { follow: false });
                this.playheadMs = t;
                this.emit("timeline-seek", { ms: t });
                this.markDirty();
            }
        });
        Object.defineProperty(this, "onPointerMove", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                const rect = this.canvas.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const t = Math.max(0, this.xToTime(x));
                switch (this.drag.kind) {
                    case "scrub":
                        this.playheadMs = t;
                        this.emit("timeline-seek", { ms: t });
                        this.markDirty();
                        break;
                    case "pan": {
                        const delta = ((this.drag.startX - x) / this.cssW) * this.viewSpanMs;
                        this.viewStartMs = Math.max(0, this.drag.startViewMs + delta);
                        this.markDirty();
                        break;
                    }
                    case "create":
                        this.drag = { ...this.drag, endMs: t };
                        this.markDirty();
                        break;
                    case "move": {
                        const dur = this.drag.origEndMs - this.drag.origStartMs;
                        let start = this.drag.origStartMs + (t - this.drag.grabMs);
                        start = Math.max(0, start);
                        this.draft = { id: this.drag.id, startMs: start, endMs: start + dur };
                        this.markDirty();
                        break;
                    }
                    case "resize": {
                        const cur = this.draft ??
                            (() => {
                                const r = this.regions.find((x2) => this.drag.kind === "resize" && x2.id === this.drag.id);
                                return r ?? null;
                            })();
                        if (!cur)
                            break;
                        let { startMs, endMs } = cur;
                        if (this.drag.edge === "start") {
                            startMs = Math.max(0, Math.min(t, endMs - MIN_REGION_MS));
                        }
                        else {
                            endMs = Math.max(t, startMs + MIN_REGION_MS);
                        }
                        this.draft = { id: this.drag.id, startMs, endMs };
                        this.markDirty();
                        break;
                    }
                    default: {
                        const hit = this.hitRegion(x);
                        if (this.canvas) {
                            this.canvas.style.cursor = hit
                                ? hit.edge
                                    ? "ew-resize"
                                    : "move"
                                : "crosshair";
                        }
                        break;
                    }
                }
            }
        });
        Object.defineProperty(this, "onPointerUp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.drag.kind === "create") {
                    const lo = Math.min(this.drag.startMs, this.drag.endMs);
                    const hi = Math.max(this.drag.startMs, this.drag.endMs);
                    if (hi - lo >= MIN_REGION_MS) {
                        this.emit("timeline-region-created", { startMs: lo, endMs: hi });
                    }
                }
                else if ((this.drag.kind === "move" || this.drag.kind === "resize") &&
                    this.draft) {
                    const base = this.regions.find((r) => r.id === this.draft.id);
                    if (base) {
                        const updated = {
                            ...base,
                            startMs: this.draft.startMs,
                            endMs: this.draft.endMs,
                        };
                        const idx = this.regions.findIndex((r) => r.id === updated.id);
                        if (idx >= 0)
                            this.regions[idx] = updated;
                        this.emit("timeline-region-updated", { region: updated });
                    }
                }
                this.draft = null;
                this.drag = { kind: "none" };
                this.markDirty();
            }
        });
        Object.defineProperty(this, "onKeyDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                if (e.key === "Delete" || e.key === "Backspace") {
                    if (this.selectedId) {
                        e.preventDefault();
                        this.emit("timeline-region-deleted", { regionId: this.selectedId });
                        this.selectedId = null;
                        this.markDirty();
                    }
                }
                else if (e.key === "Escape") {
                    this.selectedId = null;
                    this.emit("timeline-region-selected", { regionId: null });
                    this.markDirty();
                }
                else if (e.key === " ") {
                    e.preventDefault();
                    this.emit("timeline-play-toggle", {});
                }
            }
        });
    }
    connectedCallback() {
        if (!this.canvas) {
            this.style.display = "block";
            this.style.position = "relative";
            this.style.width = "100%";
            this.style.height = this.getAttribute("height") ?? "170px";
            this.style.outline = "none";
            this.tabIndex = 0;
            const canvas = document.createElement("canvas");
            canvas.style.cssText =
                "display:block;width:100%;height:100%;border-radius:10px;" +
                    "cursor:crosshair;touch-action:none";
            this.appendChild(canvas);
            const c = canvas.getContext("2d");
            if (!c) {
                console.error("[ac-live-timeline] no 2d context");
                return;
            }
            this.canvas = canvas;
            this.c2d = c;
            // ">> live": the view never moves on its own once the operator has
            // scrolled, zoomed or selected — this is the one way back to the
            // live edge (and back into follow mode).
            const live = document.createElement("button");
            live.type = "button";
            live.textContent = ">> live";
            live.title = "Jump to the live edge and follow the tape again";
            live.style.cssText =
                "position:absolute;top:6px;right:8px;z-index:2;display:none;" +
                    "padding:3px 10px;border-radius:999px;border:1px solid #22d3ee;" +
                    "background:rgba(18,50,74,.92);color:#fff;cursor:pointer;" +
                    "font:700 11px system-ui,sans-serif;letter-spacing:.04em";
            live.addEventListener("click", () => {
                this.jumpToLive();
                live.blur();
            });
            this.appendChild(live);
            this.liveBtn = live;
            canvas.addEventListener("wheel", this.onWheel, { passive: false });
            canvas.addEventListener("pointerdown", this.onPointerDown);
            canvas.addEventListener("pointermove", this.onPointerMove);
            this.addEventListener("keydown", this.onKeyDown);
        }
        window.addEventListener("pointerup", this.onPointerUp);
        window.addEventListener("pointercancel", this.onPointerUp);
        if (typeof ResizeObserver !== "undefined") {
            this.ro = new ResizeObserver(() => this.resize());
            this.ro.observe(this);
        }
        this.resize();
        this.startLoop();
    }
    disconnectedCallback() {
        window.removeEventListener("pointerup", this.onPointerUp);
        window.removeEventListener("pointercancel", this.onPointerUp);
        this.ro?.disconnect();
        this.ro = null;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
    }
    // ---- public API ---------------------------------------------------
    /** Milliseconds represented by one pushed sample (analysis packet). */
    setFrameMs(ms) {
        if (Number.isFinite(ms) && ms > 0)
            this.frameMs = ms;
    }
    /** Clear all samples/regions and rewind to a fresh take. */
    reset() {
        this.samples = [];
        this.regions = [];
        this.draft = null;
        this.selectedId = null;
        this.durationMs = 0;
        this.viewStartMs = 0;
        this.viewSpanMs = DEFAULT_SPAN_MS;
        this.playheadMs = 0;
        this.follow = true;
        this.markDirty();
    }
    /** Append one live analysis frame. */
    push(sample) {
        this.samples.push(sample);
        this.durationMs = Math.max(this.durationMs, sample.tMs + this.frameMs);
        if (this.follow) {
            this.viewStartMs = Math.max(0, this.durationMs - this.viewSpanMs);
        }
        this.markDirty();
    }
    setRegions(regions) {
        this.regions = regions;
        this.markDirty();
    }
    getRegions() {
        return this.regions;
    }
    setPlayheadMs(ms) {
        this.playheadMs = Math.max(0, ms);
        if (!this.follow)
            this.ensureVisible(this.playheadMs);
        this.markDirty();
    }
    setFollow(follow) {
        this.follow = follow;
        if (follow) {
            this.viewStartMs = Math.max(0, this.durationMs - this.viewSpanMs);
        }
        this.markDirty();
    }
    isFollowing() {
        return this.follow;
    }
    getSelectedRegionId() {
        return this.selectedId;
    }
    setSelectedRegionId(id) {
        this.selectedId = id;
        this.markDirty();
    }
    /** Zoom out so the entire take is visible. */
    fitAll() {
        this.follow = false;
        this.viewStartMs = 0;
        this.viewSpanMs = Math.max(MIN_SPAN_MS, this.durationMs || DEFAULT_SPAN_MS);
        this.markDirty();
    }
    /** Zoom the view to a time range (with padding) and stop following. */
    zoomTo(startMs, endMs, padFraction = 0.15) {
        const span = Math.max(MIN_SPAN_MS, endMs - startMs);
        const pad = span * padFraction;
        this.follow = false;
        this.viewStartMs = Math.max(0, startMs - pad);
        this.viewSpanMs = Math.max(MIN_SPAN_MS, span + pad * 2);
        this.emit("timeline-follow-changed", { follow: false });
        this.markDirty();
    }
    /** Return to the live edge and resume following the tape. */
    jumpToLive() {
        this.follow = true;
        this.viewStartMs = Math.max(0, this.durationMs - this.viewSpanMs);
        this.emit("timeline-follow-changed", { follow: true });
        this.markDirty();
    }
    syncLiveButton() {
        if (!this.liveBtn)
            return;
        this.liveBtn.style.display = this.follow ? "none" : "block";
    }
    // ---- geometry -----------------------------------------------------
    get cssW() {
        return this.canvas ? this.canvas.width / this.dpr : 1;
    }
    get cssH() {
        return this.canvas ? this.canvas.height / this.dpr : 1;
    }
    timeToX(t) {
        return ((t - this.viewStartMs) / this.viewSpanMs) * this.cssW;
    }
    xToTime(x) {
        return this.viewStartMs + (x / this.cssW) * this.viewSpanMs;
    }
    ensureVisible(t) {
        if (t < this.viewStartMs)
            this.viewStartMs = Math.max(0, t);
        else if (t > this.viewStartMs + this.viewSpanMs) {
            this.viewStartMs = Math.max(0, t - this.viewSpanMs * 0.9);
        }
    }
    resize() {
        if (!this.canvas)
            return;
        this.dpr = Math.min(2, window.devicePixelRatio || 1);
        const rect = this.getBoundingClientRect();
        const w = Math.max(1, Math.floor((rect.width || 520) * this.dpr));
        const h = Math.max(1, Math.floor((rect.height || 170) * this.dpr));
        if (this.canvas.width !== w || this.canvas.height !== h) {
            this.canvas.width = w;
            this.canvas.height = h;
        }
        this.markDirty();
    }
    markDirty() {
        this.dirty = true;
    }
    startLoop() {
        if (this.raf)
            return;
        const tick = () => {
            if (this.dirty) {
                this.draw();
                this.dirty = false;
            }
            this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    }
    firstIndexAtOrAfter(tMs) {
        let lo = 0;
        let hi = this.samples.length;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (this.samples[mid].tMs < tMs)
                lo = mid + 1;
            else
                hi = mid;
        }
        return lo;
    }
    // ---- drawing ------------------------------------------------------
    draw() {
        const ctx = this.c2d;
        if (!ctx || !this.canvas)
            return;
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        const w = this.cssW;
        const h = this.cssH;
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = "#070a16";
        ctx.fillRect(0, 0, w, h);
        this.drawClassificationBands(ctx, w, h);
        this.drawEnvelope(ctx, w, h);
        this.drawRegions(ctx, w, h);
        this.drawPlayhead(ctx, w, h);
        this.drawAxis(ctx, w, h);
        this.syncLiveButton();
    }
    drawClassificationBands(ctx, _w, h) {
        if (this.samples.length === 0)
            return;
        const viewEnd = this.viewStartMs + this.viewSpanMs;
        const start = Math.max(0, this.firstIndexAtOrAfter(this.viewStartMs) - 1);
        for (let i = start; i < this.samples.length; i++) {
            const s = this.samples[i];
            if (s.tMs > viewEnd)
                break;
            if (!s.label)
                continue;
            const next = this.samples[i + 1];
            const endT = next ? next.tMs : s.tMs + this.frameMs;
            const x0 = this.timeToX(s.tMs);
            const x1 = this.timeToX(endT);
            const hue = labelHue(s.label);
            ctx.fillStyle = `hsla(${hue},72%,52%,0.30)`;
            ctx.fillRect(x0, 0, Math.max(1, x1 - x0), h);
        }
    }
    drawEnvelope(ctx, w, h) {
        const mid = h / 2;
        ctx.strokeStyle = "rgba(255,255,255,0.10)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, mid);
        ctx.lineTo(w, mid);
        ctx.stroke();
        if (this.samples.length === 0) {
            ctx.fillStyle = "rgba(255,255,255,0.35)";
            ctx.font = "12px system-ui, sans-serif";
            ctx.textAlign = "center";
            ctx.fillText("waiting for audio…", w / 2, mid - 10);
            ctx.textAlign = "left";
            return;
        }
        const viewEnd = this.viewStartMs + this.viewSpanMs;
        const start = Math.max(0, this.firstIndexAtOrAfter(this.viewStartMs) - 1);
        const scale = mid - 6;
        ctx.fillStyle = "rgba(56,189,248,0.85)";
        ctx.beginPath();
        for (let i = start; i < this.samples.length; i++) {
            const s = this.samples[i];
            if (s.tMs > viewEnd)
                break;
            const next = this.samples[i + 1];
            const endT = next ? next.tMs : s.tMs + this.frameMs;
            const x0 = this.timeToX(s.tMs);
            const x1 = this.timeToX(endT);
            const amp = Math.max(0.6, Math.min(1, s.rms) * scale);
            ctx.rect(x0, mid - amp, Math.max(1, x1 - x0), amp * 2);
        }
        ctx.fill();
    }
    drawRegions(ctx, w, h) {
        const all = this.regions.map((r) => {
            const d = this.draft && this.draft.id === r.id ? this.draft : null;
            return {
                id: r.id,
                startMs: d ? d.startMs : r.startMs,
                endMs: d ? d.endMs : r.endMs,
                label: r.qualifiedLabel,
            };
        });
        if (this.drag.kind === "create") {
            all.push({
                id: "__pending__",
                startMs: Math.min(this.drag.startMs, this.drag.endMs),
                endMs: Math.max(this.drag.startMs, this.drag.endMs),
                label: "new region",
            });
        }
        for (const r of all) {
            const x0 = this.timeToX(r.startMs);
            const x1 = this.timeToX(r.endMs);
            if (x1 < -20 || x0 > w + 20)
                continue;
            const selected = r.id === this.selectedId;
            const hue = labelHue(r.label);
            ctx.fillStyle = selected
                ? `hsla(${hue},85%,60%,0.28)`
                : `hsla(${hue},85%,60%,0.16)`;
            ctx.fillRect(x0, 0, Math.max(1, x1 - x0), h);
            ctx.strokeStyle = selected
                ? `hsla(${hue},95%,72%,1)`
                : `hsla(${hue},85%,64%,0.85)`;
            ctx.lineWidth = selected ? 2 : 1;
            ctx.beginPath();
            ctx.moveTo(x0, 0);
            ctx.lineTo(x0, h);
            ctx.moveTo(x1, 0);
            ctx.lineTo(x1, h);
            ctx.stroke();
            ctx.fillStyle = "rgba(255,255,255,0.92)";
            ctx.font = "11px system-ui, sans-serif";
            ctx.textBaseline = "top";
            ctx.fillText(r.label, Math.max(4, x0 + 4), 4, Math.max(8, x1 - x0 - 8));
        }
    }
    drawPlayhead(ctx, w, h) {
        const x = this.timeToX(this.playheadMs);
        if (x < 0 || x > w)
            return;
        ctx.strokeStyle = "#f87171";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
        ctx.fillStyle = "#f87171";
        ctx.beginPath();
        ctx.moveTo(x - 5, 0);
        ctx.lineTo(x + 5, 0);
        ctx.lineTo(x, 8);
        ctx.closePath();
        ctx.fill();
    }
    drawAxis(ctx, _w, h) {
        const span = this.viewSpanMs;
        const stepMs = span > 60000 ? 10000 : span > 20000 ? 5000 : span > 6000 ? 1000 : 250;
        const first = Math.ceil(this.viewStartMs / stepMs) * stepMs;
        ctx.strokeStyle = "rgba(255,255,255,0.08)";
        ctx.fillStyle = "rgba(255,255,255,0.45)";
        ctx.font = "10px system-ui, sans-serif";
        ctx.textBaseline = "bottom";
        for (let t = first; t < this.viewStartMs + span; t += stepMs) {
            const x = this.timeToX(t);
            ctx.beginPath();
            ctx.moveTo(x, h - 14);
            ctx.lineTo(x, h);
            ctx.stroke();
            ctx.fillText(`${(t / 1000).toFixed(stepMs < 1000 ? 2 : 1)}s`, x + 3, h - 3);
        }
    }
    // ---- interaction ---------------------------------------------------
    hitRegion(x) {
        for (let i = this.regions.length - 1; i >= 0; i--) {
            const r = this.regions[i];
            const x0 = this.timeToX(r.startMs);
            const x1 = this.timeToX(r.endMs);
            if (Math.abs(x - x0) <= HANDLE_PX)
                return { region: r, edge: "start" };
            if (Math.abs(x - x1) <= HANDLE_PX)
                return { region: r, edge: "end" };
            if (x > x0 && x < x1)
                return { region: r, edge: null };
        }
        return null;
    }
    emit(type, detail) {
        this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
    }
}
if (!customElements.get("ac-live-timeline")) {
    customElements.define("ac-live-timeline", AcLiveTimeline);
}
